/**
 * Bars and slots in a V4 song.
 *
 * A row holds bars; a bar holds the slots played in order within one
 * measure. Today every bar holds exactly one slot (the beats feature will let
 * a bar be split), so "the slots of a row" is still the flat list the editors
 * show, and adding or removing a slot adds or removes a bar. Everything that
 * walks or edits slots goes through here, so the bar structure has one home
 * when bars start holding more than one slot.
 */
/** Random ids, for callers that want fresh ids rather than derived ones. */
export const defaultSchemaIdGenerator = kind => `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
/**
 * The id a bar gets when it is created around a slot: derived from that
 * slot's id. Slot ids are unique within a song, so bar ids are too, and a
 * song converted to V3 and back gets the same bar ids, which keeps
 * save → load round trips and tests deterministic. It is only a naming
 * convention at creation; nothing requires the id to keep matching later.
 */
export const barIdForSlot = (slotId) => `bar:${slotId}`;
/**
 * A bar holding just this slot: what every slot is until bars can be split.
 * Without a generator the bar id is derived from the slot id (`barIdForSlot`).
 */
export const createBar = (slot, generateId) => ({
    id: generateId ? generateId('bar') : barIdForSlot(slot.id),
    slots: [slot],
});
/** A row's slots in playing order, across its bars: the flat list the editors show. */
export const getRowSlots = (row) => row.bars.flatMap(bar => bar.slots);
export const getRowSlotCount = (row) => row.bars.reduce((count, bar) => count + bar.slots.length, 0);
/** Every slot of every row, in order. */
export const getAllSlots = (sections) => sections.flatMap(section => section.rows.flatMap(row => getRowSlots(row)));
/** A row whose bars each hold one of `slots`, in order. */
export const rowFromSlots = (id, slots, generateId, timeSignature = null) => ({ id, bars: slots.map(slot => createBar(slot, generateId)), timeSignature });
/** The same row with every slot mapped (index counts across the row). Bars and other row fields are kept. */
export const mapRowSlots = (row, fn) => {
    let index = 0;
    return { ...row, bars: row.bars.map(bar => ({ ...bar, slots: bar.slots.map(slot => fn(slot, index++)) })) };
};
/** Every slot of every section mapped; the containers are copied, nothing else changes. */
export const mapSlots = (sections, fn) => sections.map(section => ({ ...section, rows: section.rows.map(row => mapRowSlots(row, slot => fn(slot))) }));
/** Where a slot lives, or null when no row holds it. */
export const findSlot = (sections, slotId) => {
    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
        const section = sections[sectionIndex];
        for (let rowIndex = 0; rowIndex < section.rows.length; rowIndex++) {
            const row = section.rows[rowIndex];
            for (let barIndex = 0; barIndex < row.bars.length; barIndex++) {
                const bar = row.bars[barIndex];
                const slotIndex = bar.slots.findIndex(slot => slot.id === slotId);
                if (slotIndex !== -1) {
                    return { sectionIndex, rowIndex, barIndex, slotIndex, section, row, bar, slot: bar.slots[slotIndex] };
                }
            }
        }
    }
    return null;
};
const barIndexOfSlot = (row, slotId) => row.bars.findIndex(bar => bar.slots.some(slot => slot.id === slotId));
/**
 * A new slot in a new bar of its own, right after (or before) the bar holding
 * the given slot. The row is returned unchanged when that slot isn't in it.
 */
export const insertSlotInRow = (row, slot, position, generateId) => {
    const anchor = 'after' in position ? position.after : position.before;
    const barIndex = barIndexOfSlot(row, anchor);
    if (barIndex === -1) {
        return row;
    }
    const at = 'after' in position ? barIndex + 1 : barIndex;
    const bars = [...row.bars];
    bars.splice(at, 0, createBar(slot, generateId));
    return { ...row, bars };
};
/**
 * The row without the slot. A bar left with no slots is dropped, so removing
 * a slot removes its bar today. The row is returned unchanged when the slot
 * isn't in it; keeping at least one slot per row is the caller's rule.
 */
export const removeSlotFromRow = (row, slotId) => {
    const barIndex = barIndexOfSlot(row, slotId);
    if (barIndex === -1) {
        return row;
    }
    const bar = row.bars[barIndex];
    const slots = bar.slots.filter(slot => slot.id !== slotId);
    const bars = [...row.bars];
    if (slots.length === 0) {
        bars.splice(barIndex, 1);
    }
    else {
        bars[barIndex] = { ...bar, slots };
    }
    return { ...row, bars };
};
/** The row with one slot replaced; unchanged (same reference) when the slot isn't in it. */
export const updateSlotInRow = (row, slotId, update) => {
    const barIndex = barIndexOfSlot(row, slotId);
    if (barIndex === -1) {
        return row;
    }
    const bars = [...row.bars];
    const bar = bars[barIndex];
    bars[barIndex] = { ...bar, slots: bar.slots.map(slot => (slot.id === slotId ? update(slot) : slot)) };
    return { ...row, bars };
};
/** The sections with one slot replaced, copying only the containers on its path. Same array when not found. */
export const updateSlotInSections = (sections, slotId, update) => {
    const location = findSlot(sections, slotId);
    if (!location) {
        return sections;
    }
    return sections.map((section, si) => si !== location.sectionIndex
        ? section
        : {
            ...section,
            rows: section.rows.map((row, ri) => (ri !== location.rowIndex ? row : updateSlotInRow(row, slotId, update))),
        });
};
/** Ids used more than once across sections, rows, bars and slots (each listed once). */
export const findDuplicateIds = (sections) => {
    const seen = new Set();
    const duplicates = new Set();
    const see = (id) => {
        if (seen.has(id)) {
            duplicates.add(id);
        }
        seen.add(id);
    };
    for (const section of sections) {
        see(section.id);
        for (const row of section.rows) {
            see(row.id);
            for (const bar of row.bars) {
                see(bar.id);
                for (const slot of bar.slots) {
                    see(slot.id);
                }
            }
        }
    }
    return [...duplicates];
};
